import { api } from './api.js'
import { toast } from './components/app-toast.js'
import './components/wmj-picker.js'

// Thrown when the agent dismisses a picker instead of linking — lets callers
// abort quietly (no error toast) while still refreshing to reflect any partial
// linking already done.
export class WmjCancelled extends Error {
  constructor() {
    super('Workamajig linking cancelled')
    this.name = 'WmjCancelled'
  }
}

// Show the picker modal, resolving to the chosen record or null on cancel. Exported
// so callers that collect a selection without saving it yet (e.g. triage) can reuse
// the same UI as the ticket sidebar.
export function openPicker({ mode, clientId, jobNumber }) {
  return new Promise(resolve => {
    const el = document.createElement('wmj-picker')
    el.mode = mode
    if (clientId) el.clientId = clientId
    if (jobNumber) el.jobNumber = jobNumber
    const close = (val) => { el.remove(); resolve(val) }
    el.addEventListener('pick', e => close(e.detail))
    el.addEventListener('cancel', () => close(null))
    document.body.appendChild(el)
  })
}

// Open the company picker and set the ticket's client to the chosen Workamajig
// company. The backend resolves the company to (or creates) the linked client org
// (§18). Changing to a different company clears the ticket's job, since jobs are
// scoped per company and the old one no longer applies. Returns true if a company
// was picked, false if the agent cancelled. ctx is { ticketNumber, client }.
export async function pickClientForTicket({ ticketNumber, client }) {
  const company = await openPicker({ mode: 'company' })
  if (!company) return false
  const patch = { workamajigCompanyId: company.clientId }
  if (company.clientId !== (client?.workamajigCompanyId || null)) patch.workamajigJobNumber = ''
  await api.patch(`/tickets/${ticketNumber}`, patch)
  toast(`Client set to ${company.name}`, 'success')
  return true
}

// Open the job picker (scoped to the ticket's linked company) and set the ticket's
// job. Returns true if a job was picked, false if the agent cancelled. The caller
// ensures the ticket already has a Workamajig-linked client (the job feed is scoped
// by company). ctx is { ticketNumber, client }.
export async function pickJobForTicket({ ticketNumber, client }) {
  const job = await openPicker({ mode: 'job', clientId: client?.workamajigCompanyId || null })
  if (!job) return false
  await api.patch(`/tickets/${ticketNumber}`, { workamajigJobNumber: job.jobNumber })
  toast(`Job #${job.jobNumber} assigned`, 'success')
  return true
}

// Open the task picker (scoped to the ticket's job) and set the ticket's task — the
// budget line its hours belong to (§19.1). Returns true if a task was picked.
export async function pickTaskForTicket({ ticketNumber, jobNumber }) {
  if (!jobNumber) { toast('Assign a job first', 'info'); return false }
  const task = await openPicker({ mode: 'task', jobNumber })
  if (!task) return false
  await api.patch(`/tickets/${ticketNumber}`, { workamajigTaskId: task.taskId })
  toast(`Task “${task.name}” selected`, 'success')
  return true
}

// Run an agent ticket action, transparently resolving first-touch Workamajig linking
// (§18.3): on WMJ_CLIENT_REQUIRED / WMJ_JOB_REQUIRED / WMJ_TASK_REQUIRED it prompts
// the required picker, performs the link, then retries the original action. The order
// is forced by scoping — the job picker needs the company, the task picker the job.
//   action: () => Promise                        the api call to (re)run
//   ctx:    { ticketNumber, client, jobNumber }  client = { id, name, workamajigCompanyId }
export async function runTicketAction(action, ctx) {
  let companyKey = ctx.client?.workamajigCompanyId || null
  let jobNumber = ctx.jobNumber || null
  // At most three links (company, job, task) precede the retried action.
  for (let i = 0; i < 5; i++) {
    try {
      return await action()
    } catch (err) {
      if (err.code === 'WMJ_CLIENT_REQUIRED') {
        if (!ctx.client?.id) throw err   // e.g. an unresolved triage ticket
        const company = await openPicker({ mode: 'company' })
        if (!company) throw new WmjCancelled()
        const updated = await api.patch(`/clients/${ctx.client.id}`, { workamajigCompanyId: company.clientId })
        companyKey = updated.workamajigCompanyId
        toast(`Linked ${updated.name} to Workamajig`, 'success')
        continue
      }
      if (err.code === 'WMJ_JOB_REQUIRED') {
        const job = await openPicker({ mode: 'job', clientId: companyKey })
        if (!job) throw new WmjCancelled()
        await api.patch(`/tickets/${ctx.ticketNumber}`, { workamajigJobNumber: job.jobNumber })
        jobNumber = job.jobNumber
        toast(`Job #${job.jobNumber} assigned`, 'success')
        continue
      }
      if (err.code === 'WMJ_TASK_REQUIRED') {
        const task = await openPicker({ mode: 'task', jobNumber })
        if (!task) throw new WmjCancelled()
        await api.patch(`/tickets/${ctx.ticketNumber}`, { workamajigTaskId: task.taskId })
        toast(`Task “${task.name}” selected`, 'success')
        continue
      }
      throw err
    }
  }
  throw new Error('Could not resolve Workamajig linking')
}
